<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
import WootSnackbar from './Snackbar.vue';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const props = defineProps({
  duration: {
    type: Number,
    default: 5000,
  },
});

const TOAST_VARIANTS = new Set([
  'success',
  'positive',
  'error',
  'alert',
  'warning',
  'info',
]);

const normalizeToastAction = action => {
  if (!action) return null;

  if (typeof action === 'string') {
    return TOAST_VARIANTS.has(action)
      ? { variant: action }
      : { message: action };
  }

  if (typeof action !== 'object') return null;

  const normalized = { ...action };
  const inferredVariant =
    normalized.variant ||
    normalized.status ||
    normalized.level ||
    normalized.kind ||
    normalized.toastType;

  if (TOAST_VARIANTS.has(inferredVariant)) {
    normalized.variant = inferredVariant;
  } else if (TOAST_VARIANTS.has(normalized.type)) {
    normalized.variant = normalized.type;
  }

  return normalized;
};

const { t } = useI18n();

const snackMessages = ref([]);
const snackbarContainer = ref(null);
const notificationStyle = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.NOTIFICATION_GROUP_STYLE) || 'expanded'
);
const notificationPosition = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.NOTIFICATION_POSITION) || 'top-right'
);
const isStackHovered = ref(false);
let stackCollapseTimer = null;
const transitionName = computed(() => 'toast-slide');
const listClass = computed(() => [
  'pointer-events-none',
  `toast-list--${notificationStyle.value}`,
  isStackHovered.value && notificationStyle.value === 'stacked'
    ? 'toast-list--stack-open'
    : '',
  notificationPosition.value.startsWith('bottom') ? 'toast-list--bottom' : '',
]);

const containerPositionClass = computed(() => {
  const positions = {
    'top-left': 'left-4 top-4 right-auto bottom-auto sm:left-6 sm:top-6',
    'top-center':
      'left-1/2 top-4 right-auto bottom-auto -translate-x-1/2 sm:top-6',
    'top-right': 'right-4 top-4 left-auto bottom-auto sm:right-6 sm:top-6',
    'middle-left':
      'left-4 top-1/2 right-auto bottom-auto -translate-y-1/2 sm:left-6',
    center:
      'left-1/2 top-1/2 right-auto bottom-auto -translate-x-1/2 -translate-y-1/2',
    'middle-right':
      'right-4 top-1/2 left-auto bottom-auto -translate-y-1/2 sm:right-6',
    'bottom-left': 'left-4 bottom-4 right-auto top-auto sm:left-6 sm:bottom-6',
    'bottom-center':
      'left-1/2 bottom-4 right-auto top-auto -translate-x-1/2 sm:bottom-6',
    'bottom-right':
      'right-4 bottom-4 left-auto top-auto sm:right-6 sm:bottom-6',
  };

  return positions[notificationPosition.value] || positions['top-right'];
});

const syncNotificationStyle = () => {
  notificationStyle.value =
    LocalStorage.get(LOCAL_STORAGE_KEYS.NOTIFICATION_GROUP_STYLE) || 'expanded';
  notificationPosition.value =
    LocalStorage.get(LOCAL_STORAGE_KEYS.NOTIFICATION_POSITION) || 'top-right';
  isStackHovered.value = false;
};

const clearStackCollapseTimer = () => {
  if (!stackCollapseTimer) return;
  window.clearTimeout(stackCollapseTimer);
  stackCollapseTimer = null;
};

const expandStack = () => {
  clearStackCollapseTimer();
  if (notificationStyle.value === 'stacked') {
    isStackHovered.value = true;
  }
};

const scheduleStackCollapse = () => {
  clearStackCollapseTimer();
  stackCollapseTimer = window.setTimeout(() => {
    isStackHovered.value = false;
    stackCollapseTimer = null;
  }, 300);
};

const showPopover = () => {
  try {
    const el = snackbarContainer.value;
    if (el?.matches(':popover-open')) {
      el.hidePopover();
    }
    el?.showPopover();
  } catch (e) {
    // ignore
  }
};

const removeToast = key => {
  snackMessages.value = snackMessages.value.filter(item => item.key !== key);
};

const onDismissToastMessage = id => {
  if (!id) return;
  snackMessages.value = snackMessages.value.filter(
    item => item.action?.id !== id
  );
};

const onNewToastMessage = ({ message: originalMessage, action }) => {
  const normalizedAction = normalizeToastAction(action);
  let message = originalMessage;
  if (normalizedAction?.usei18n) {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    message = t(originalMessage);
  }
  const duration = normalizedAction?.duration || props.duration;
  const key = `${Date.now()}-${Math.random()}`;

  snackMessages.value.unshift({
    key,
    message,
    action: normalizedAction,
    duration,
  });

  nextTick(showPopover);
};

onMounted(() => {
  emitter.on('newToastMessage', onNewToastMessage);
  emitter.on('dismissToastMessage', onDismissToastMessage);
  window.addEventListener(
    'appearance-preferences-changed',
    syncNotificationStyle
  );
});

onUnmounted(() => {
  clearStackCollapseTimer();
  emitter.off('newToastMessage', onNewToastMessage);
  emitter.off('dismissToastMessage', onDismissToastMessage);
  window.removeEventListener(
    'appearance-preferences-changed',
    syncNotificationStyle
  );
});
</script>

<template>
  <div
    ref="snackbarContainer"
    popover="manual"
    class="pointer-events-none fixed z-[9999] m-0 w-[calc(100%-2rem)] max-w-[24rem] overflow-visible border-0 bg-transparent p-0 outline-none backdrop:bg-transparent"
    :class="containerPositionClass"
  >
    <transition-group
      :name="transitionName"
      tag="div"
      :class="listClass"
      @mouseenter="expandStack"
      @mouseleave="scheduleStackCollapse"
    >
      <WootSnackbar
        v-for="snackMessage in snackMessages"
        :key="snackMessage.key"
        :message="snackMessage.message"
        :action="snackMessage.action"
        :duration="snackMessage.duration"
        :paused="isStackHovered && notificationStyle === 'stacked'"
        @dismiss="removeToast(snackMessage.key)"
      />
    </transition-group>
  </div>
</template>

<style scoped>
.toast-slide-enter-active,
.toast-slide-leave-active,
.toast-slide-move {
  transition:
    opacity 180ms ease,
    transform 220ms cubic-bezier(0.21, 1.02, 0.73, 1);
}

.toast-slide-enter-from,
.toast-slide-leave-to {
  opacity: 0;
  transform: translate3d(1.5rem, -0.5rem, 0) scale(0.98);
}

.toast-slide-leave-active {
  position: absolute;
  right: 0;
  width: 100%;
}

.toast-list--stacked {
  position: relative;
  min-height: 5.5rem;
}

.toast-list--stacked :deep(.snackbar-card) {
  position: absolute;
  inset: 0 0 auto;
  margin-bottom: 0;
  transform-origin: top center;
  transition:
    opacity 180ms ease,
    transform 220ms cubic-bezier(0.21, 1.02, 0.73, 1);
}

.toast-list--stacked :deep(.snackbar-card:nth-child(1)) {
  z-index: 3;
}

.toast-list--stacked :deep(.snackbar-card:nth-child(2)) {
  z-index: 2;
  opacity: 0.72;
  transform: translateY(0.55rem) scale(0.96);
}

.toast-list--stacked :deep(.snackbar-card:nth-child(3)) {
  z-index: 1;
  opacity: 0.42;
  transform: translateY(1rem) scale(0.92);
}

.toast-list--stacked :deep(.snackbar-card:nth-child(n + 4)) {
  visibility: hidden;
}

.toast-list--stacked.toast-list--stack-open :deep(.snackbar-card) {
  position: relative;
  inset: auto;
  visibility: visible;
  opacity: 1;
  transform: none;
}

.toast-list--stacked.toast-list--stack-open {
  pointer-events: auto;
}

.toast-list--stacked.toast-list--stack-open
  :deep(.snackbar-card:not(:last-child)) {
  margin-bottom: 0.75rem;
}

.toast-list--bottom.toast-list--stacked:not(.toast-list--stack-open)
  :deep(.snackbar-card:nth-child(2)) {
  transform: translateY(-0.55rem) scale(0.96);
}

.toast-list--bottom.toast-list--stacked:not(.toast-list--stack-open)
  :deep(.snackbar-card:nth-child(3)) {
  transform: translateY(-1rem) scale(0.92);
}
</style>
